import { useEffect, useMemo, useRef, useState } from "react";
import type { TargetRow } from "@empire/shared";
import { MapView, type MapVillage } from "../../pixi/MapView.js";
import { useAttackStore } from "../../store/attack.js";
import { useSessionStore } from "../../store/session.js";
import { useUiStore } from "../../store/ui.js";
import { DIFFICULTY_STYLE, TargetList } from "./TargetList.js";
import { protectionLeft } from "../components/LootLine.js";

/**
 * The Map tab (Sep 2026): the world, with your village in the middle.
 *
 * Tap a village to see who holds it and attack it; the list is one tap away
 * for anyone who would rather scan than look. Phase 0 has no travel time, so
 * distance is shown for orientation only — it costs nothing yet.
 */
export function WorldMap(): JSX.Element {
  const [mode, setMode] = useState<"map" | "list">("map");
  const load = useAttackStore((s) => s.loadTargets);
  const loading = useAttackStore((s) => s.loadingTargets);
  const error = useAttackStore((s) => s.targetsError);
  const sessionStatus = useSessionStore((s) => s.status);

  useEffect(() => {
    if (sessionStatus === "ready") void load();
  }, [sessionStatus, load]);

  return (
    <div className="flex h-full flex-col">
      <header className="empire-parchment flex shrink-0 items-center justify-between gap-3 border-b border-panel-edge px-3 py-1.5">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-gold">Ancient frontier</p>
          <h2 className="font-display text-lg text-ink">World</h2>
          <p className="hidden text-[11px] text-ink-dim sm:block">
            Tap a village to attack it. Barbarians are for practice.
          </p>
        </div>
        <div
          className="flex shrink-0 overflow-hidden rounded-lg border border-panel-edge"
          role="tablist"
        >
          {(["map", "list"] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              data-testid={`map-mode-${m}`}
              onClick={() => setMode(m)}
              className={`h-11 px-3 text-xs font-semibold ${mode === m ? "bg-gold/20 text-gold" : "bg-panel text-ink-dim"}`}
            >
              {m === "map" ? "Map" : "List"}
            </button>
          ))}
        </div>
      </header>
      {error ? (
        <p className="mx-3 mt-2 rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-[11px] text-warn">
          {error}{" "}
          <button
            type="button"
            className="underline"
            onClick={() => void load()}
            disabled={loading}
          >
            Try again
          </button>
        </p>
      ) : null}
      <div className="relative min-h-0 flex-1">
        {mode === "map" ? <MapCanvas /> : <TargetList header={false} />}
      </div>
    </div>
  );
}

const HOME = "home";

function MapCanvas(): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<MapView | null>(null);
  const framed = useRef(false);
  const targets = useAttackStore((s) => s.targets);
  const home = useAttackStore((s) => s.home);
  const [selected, setSelected] = useState<string | null>(null);

  const villages = useMemo(() => toVillages(targets, home), [targets, home]);
  const villagesRef = useRef(villages);
  villagesRef.current = villages;

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let cancelled = false;
    let mounted: MapView | undefined;
    void MapView.mount(el).then((v) => {
      if (cancelled) {
        v.destroy();
        return;
      }
      mounted = v;
      view.current = v;
      if (import.meta.env.DEV) (window as unknown as { __map?: MapView }).__map = v;
      v.onTap((id) => {
        setSelected(id);
        v.select(id);
      });
      v.setVillages(villagesRef.current);
      if (villagesRef.current.length > 0) {
        v.frame();
        framed.current = true;
      }
    });
    return () => {
      cancelled = true;
      view.current = null;
      framed.current = false;
      mounted?.destroy();
    };
  }, []);

  useEffect(() => {
    const v = view.current;
    if (!v) return;
    v.setVillages(villages);
    // Frame once, when the world first arrives; after that the player's own
    // panning is theirs.
    if (!framed.current && villages.length > 0) {
      v.frame();
      framed.current = true;
    }
  }, [villages]);

  const chosen = villages.find((v) => v.id === selected) ?? null;
  const target = targets.find((t) => t.villageId === selected) ?? null;

  return (
    <div className="absolute inset-0">
      <div
        ref={host}
        className="absolute inset-0 touch-none [&>canvas]:block"
        role="img"
        aria-label="World map. Drag to explore, pinch or use the buttons to zoom, and tap a village for its actions. The List tab also shows all targets."
        data-testid="map-canvas"
      />
      <Legend />
      <div
        className="pointer-events-none absolute left-4 top-3 flex flex-col items-center text-xs text-ink [text-shadow:0_1px_4px_#172719]"
        aria-label="North is up"
      >
        <span>N</span>
        <span className="text-2xl" aria-hidden="true">
          ↑
        </span>
      </div>
      <div className="absolute right-3 top-3 flex flex-col gap-2">
        <MapButton label="＋" title="Zoom in" onClick={() => view.current?.zoomBy(1.4)} />
        <MapButton label="－" title="Zoom out" onClick={() => view.current?.zoomBy(1 / 1.4)} />
        {home ? (
          <MapButton
            label="⌂"
            title="Back to my village"
            testId="map-home"
            onClick={() => view.current?.goHome(home.x, home.y)}
          />
        ) : null}
      </div>
      {chosen ? (
        <VillageSheet
          village={chosen}
          target={target}
          home={home}
          onClose={() => {
            setSelected(null);
            view.current?.select(null);
          }}
        />
      ) : null}
    </div>
  );
}

function toVillages(
  targets: readonly TargetRow[],
  home: { x: number; y: number } | null
): MapVillage[] {
  const out: MapVillage[] = targets.map((t) => ({
    id: t.villageId,
    x: t.x,
    y: t.y,
    name: t.villageName,
    kind: t.difficulty ? "npc" : "player",
    difficulty: t.difficulty,
    beaten: t.breachedByMe,
  }));
  if (home)
    out.push({
      id: HOME,
      x: home.x,
      y: home.y,
      name: "You",
      kind: "home",
      difficulty: null,
      beaten: false,
    });
  return out;
}

function Legend(): JSX.Element {
  const dot = (color: string, mark: string, text: string) => (
    <span className="flex items-center gap-1">
      <span style={{ color }} aria-hidden="true">
        {mark}
      </span>
      {text}
    </span>
  );
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-wrap gap-x-4 gap-y-1 border-t border-panel-edge bg-bg-raised/95 px-4 py-2 text-[11px] text-ink-dim">
      {dot("#f2b33d", "◆", "Your village")}
      {dot("#5b8fd9", "●", "Players")}
      {dot("#e2564a", "▲", "Barbarians")}
    </div>
  );
}

function MapButton({
  label,
  title,
  onClick,
  testId,
}: {
  label: string;
  title: string;
  onClick: () => void;
  testId?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      data-testid={testId}
      onClick={onClick}
      className="empire-control h-11 w-11 text-lg"
    >
      {label}
    </button>
  );
}

function VillageSheet({
  village,
  target,
  home,
  onClose,
}: {
  village: MapVillage;
  target: TargetRow | null;
  home: { x: number; y: number } | null;
  onClose: () => void;
}): JSX.Element {
  const confirm = useAttackStore((s) => s.confirm);
  const setTab = useUiStore((s) => s.setTab);
  const away = home ? Math.round(Math.hypot(village.x - home.x, village.y - home.y)) : null;

  return (
    <div
      className="empire-parchment empire-sheet absolute inset-x-0 bottom-0 z-10 rounded-t-2xl border-t border-gold/40 bg-bg-raised px-4 pb-4 pt-3 shadow-2xl"
      data-testid="map-sheet"
    >
      <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-panel-edge" />
      {village.kind === "home" ? (
        <>
          <h3 className="text-base font-bold text-ink">Your village</h3>
          <p className="text-xs text-ink-dim">
            ({village.x} | {village.y}) · everyone else is measured from here
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setTab("village")}
              className="h-12 flex-1 rounded-xl bg-gold text-sm font-bold text-bg"
            >
              Open village
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-12 rounded-xl border border-panel-edge px-4 text-sm text-ink-dim"
            >
              Close
            </button>
          </div>
        </>
      ) : target ? (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-base font-bold text-ink">{target.villageName}</h3>
              <p className="truncate text-xs text-ink-dim">
                {target.playerName} · ({target.x} | {target.y})
                {away !== null ? ` · ${away} squares away` : ""}
              </p>
            </div>
            {target.difficulty ? (
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${DIFFICULTY_STYLE[target.difficulty]}`}
              >
                {target.difficulty}
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-[11px] text-ink-dim">
            Attacked {target.attackedCount} {target.attackedCount === 1 ? "time" : "times"}
            {target.breachedByMe ? " · you broke through before" : ""}
          </p>
          {target.rewardNote ? (
            <p className="mt-1 text-[11px] text-ink-dim">{target.rewardNote}</p>
          ) : null}
          {protectionLeft(target.attackBlockedUntil ?? target.protectedUntil) ? (
            <p className="mt-1 text-[11px] text-gold" data-testid="map-protected">
              {target.attackBlockReason ?? "Beginner protection is active."}{" "}
              {protectionLeft(target.attackBlockedUntil ?? target.protectedUntil)} remaining.
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              data-testid="map-attack"
              disabled={protectionLeft(target.attackBlockedUntil ?? target.protectedUntil) !== null}
              onClick={() => confirm(target)}
              className="h-12 flex-1 rounded-xl bg-gold text-sm font-bold text-bg disabled:opacity-40"
            >
              {protectionLeft(target.attackBlockedUntil ?? target.protectedUntil)
                ? "Protected"
                : "Attack"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-12 rounded-xl border border-panel-edge px-4 text-sm text-ink-dim"
            >
              Close
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
